import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AskUserQuestionTool } from 'js/src/agent/tools/AskUserQuestionTool';
import { CreateBlankVideoTool } from 'js/src/agent/tools/CreateBlankVideoTool';
import { SearchVoicesTool } from 'js/src/agent/tools/SearchVoicesTool';

export const gatherRequirementsSkill: Skill = {
    name: SKILL_NAMES.gatherRequirements,
    description:
        'Load BEFORE creating any new video or asking multiple questions for how to collect the brief.',
    kind: 'howto',
    surfaces: ['sketch'],
    // Getter, not a plain string, so tool-name interpolation is deferred past the
    // skills↔tools import cycle (see changeTemplate.ts).
    get instructions(): string {
        return `# Gather Requirements First

Before building a new video, open with ONE questionnaire that collects the whole
brief at once. Everything downstream — template, script, media, pacing — is
expensive to redo, so a single round of questions up front is far cheaper than
guessing and rebuilding.

## The rules that matter
- Call \`${AskUserQuestionTool.toolName}\` once with all questions together.
- Ask before \`${CreateBlankVideoTool.toolName}\` to show all the questions as a full-pane questionnaire.
- Do not ask about things the user has explicitly specified, unless what they
  asked for isn't possible (such as a duration over the limit).
- Research before you ask. If the user gave a topic or script, search media and
  sources first to confirm you can source what it needs. Ask about content you
  cannot find.
- Keep questions to 10 words or fewer.
- Do NOT add a "Decide for me" / "Surprise me" choice. Every question is
  already skippable.
- Do NOT write your own "Other", "Something else" or "None of the above"
  choice. Radio and checkbox questions get a free-text "Other" row
  automatically, and yours is dropped as a duplicate.

## Script details
If the user doesn't provide a full script or plan, help understand what the video should be about:
- What's the core story? Ask a radio question with a few suggested angles
- Who's the audience?
- Any specific numbers or claims you want to include?
- Duration

## Always ask unless explicitly stated
- Aspect ratio
- Template: which template/look to use. Use \`type: "template"\` and offer a few
  suggested templates as options (label = template id)
- Voiceover: what voice to use, if any. Use \`type: "voice"\`. Call
  \`${SearchVoicesTool.toolName}\` FIRST to find voices matching the content's language and tone,
  then pass their \`slug\` values as the option labels — one option per voice.
  The label must be the slug, not a description: an unknown id is dropped and the
  user sees generic defaults instead. Pass no options only if the search fails.

## Ask for content you can't source yourself
Search first, then ask only for what the search couldn't produce. The build is
only as good as the material behind it, so ask if the user has content to use:
- **Media** — the most common gap. A specific brand, product, person, or niche
  topic rarely has usable stock footage. If a media search comes back with
  nothing that actually shows the subject, ask the user to upload their own
  images or clips rather than building the video around generic substitutes.
  Use \`type: "file_upload"\`
- If the user asks for a video that requires a recorded voiceover or footage but doesn't provide,
    ask for it. Use \`type: "file_upload"\`
- **Brand** — \`type: "brand"\` when the video seems like it should be branded, to get the brand
  details from the user.
- **Call to action** — when the video seems to needs to end on one (a URL, an
  offer, a next step) and the user hasn't said what it is. Suggest a few options.

## User dismissal
If the user dismissed the questionnaire without answering, don't re-ask and proceed on
your best judgment.`;
    },
};
